// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Emoji riêng của Z-Meet, cộng emoji Unicode thông thường.
//
// File này KHÔNG có JSX: phần dựng giao diện nằm ở emoji.tsx. Tách ra để bộ
// phân tích chạy được dưới `node --test` mà không cần một bộ biên dịch JSX —
// và nó là phần duy nhất ở đây có logic đáng test.
//
// # Ba cách gõ, một ô nhập
//
//	:like:        emoji RIÊNG của Z-Meet  → ảnh động
//	:tada: :+1:   shortcode chuẩn         → emoji Unicode
//	:) :D <3      mặt cười kiểu chữ       → emoji Unicode
//	👍            emoji Unicode gõ thẳng   → giữ nguyên
//
// Thứ tự ưu tiên là ĐÓNG và quan trọng: emoji riêng thắng shortcode chuẩn. Danh
// mục riêng có `:smile:` và `:heart:`, và bộ chuẩn cũng có — người dùng gõ
// `:smile:` trong Z-Meet phải nhận emoji của Z-Meet, nếu không thì việc có bộ
// riêng mất ý nghĩa.
//
// Emoji Unicode gõ thẳng KHÔNG bị đụng tới: trình duyệt đã vẽ đúng, và can
// thiệp vào nó là tự nhận việc vẽ lại toàn bộ bảng emoji của mọi nền tảng.
//
// # Vì sao ảnh được bundle chứ không nạp từ CDN
//
// CONTRIBUTING yêu cầu client thật bundle asset, và ADR-0001 §1 nói tới môi
// trường air-gapped. Một phòng họp mà biểu cảm hiện thành ô vuông vỡ vì máy
// không ra được Internet là lỗi nhìn thấy ngay.
//
// # Vì sao WebP 128px chứ không phải GIF gốc
//
// Bản gốc là GIF 512×512, tổng 7,1 MB cho chín cái — cho thứ hiển thị ở 24–48px.
// Chuyển sang WebP động 128px còn 1,2 MB, vẫn sắc nét ở màn hình 2×. Sáu MB đó
// là sáu MB mà mỗi người dùng tải trên mạng di động.

import { emojify } from 'node-emoji'
import { emoticon } from 'emoticon'

export type CustomEmoji = {
  /** Tên dùng trong `:tên:` và trên dây. */
  name: string
  /** Emoji Unicode tương đương, dùng làm alt và làm thứ hiện ra khi ảnh hỏng. */
  fallback: string
}

/**
 * CATALOG là danh sách ĐÓNG.
 *
 * Reaction gửi tên trong danh sách này, không gửi chuỗi tuỳ ý. Cho gõ tự do
 * nghĩa là cho phép hiện một chuỗi bất kỳ nổi trên màn hình của mọi người —
 * một kênh chat thứ hai không ai kiểm duyệt được, và nó luôn bị lạm dụng.
 */
export const CATALOG: CustomEmoji[] = [
  { name: 'like', fallback: '👍' },
  { name: 'clap', fallback: '👏' },
  { name: 'laughing', fallback: '😂' },
  { name: 'smile', fallback: '🙂' },
  { name: 'heart', fallback: '😘' },
  { name: 'party', fallback: '🎉' },
  { name: 'fire', fallback: '🔥' },
  { name: 'hundred', fallback: '💯' },
  { name: 'cry', fallback: '😭' },
]

const BY_NAME = new Map(CATALOG.map((e) => [e.name, e]))

export function isCustom(name: string): boolean {
  return BY_NAME.has(name)
}

export function emojiURL(name: string): string {
  return `/emoji/${name}.webp`
}

// Chỉ khớp `:tên:` với tên gồm chữ thường, số và gạch dưới. Không dùng biểu
// thức tham lam: `:a: và :b:` phải cho hai token, không phải một.
const TOKEN = /:([a-z0-9_]+):/g

/**
 * parse tách văn bản thành các đoạn chữ và các emoji riêng.
 *
 * Token không có trong danh mục được GIỮ NGUYÊN dạng chữ. Một người viết
 * "12:30: hẹn gặp" không bị mất đoạn giữa, và một shortcode gõ sai hiện ra để
 * người viết thấy mà sửa — im lặng nuốt nó đi thì họ không bao giờ biết.
 */
export function parse(text: string): Array<{ t: 'text'; v: string } | { t: 'emoji'; v: string }> {
  const out: Array<{ t: 'text'; v: string } | { t: 'emoji'; v: string }> = []
  const push = (v: string) => {
    if (v) out.push({ t: 'text', v: expandText(v) })
  }

  let last = 0
  for (const m of text.matchAll(TOKEN)) {
    const name = m[1]!
    // Emoji riêng thắng shortcode chuẩn cùng tên — xem ghi chú đầu file.
    if (!BY_NAME.has(name)) continue
    push(text.slice(last, m.index))
    out.push({ t: 'emoji', v: name })
    last = m.index + m[0].length
  }
  push(text.slice(last))
  return out
}

/**
 * EMOTICON khớp mặt cười kiểu chữ, và CHỈ khi nó đứng riêng một từ.
 *
 * ⚠️ Ranh giới là phần khó, không phải danh sách. Không có nó thì:
 *
 *	https://x.com   → `:/` thành 😕 giữa một đường link
 *	C:\Users        → `:\` thành một mặt cười giữa đường dẫn
 *	12:30           → không sao, nhưng 8:D thì thành 8😃
 *
 * Sắp xếp theo độ dài GIẢM DẦN để `:-)` được thử trước `:)`; nếu không, `:)`
 * khớp trước và bỏ lại dấu gạch nối lơ lửng.
 *
 * Dùng nhóm bắt cho khoảng trắng phía trước thay vì lookbehind: Safari mới hỗ
 * trợ lookbehind từ 16.4, và không có lý do gì để loại người dùng Safari cũ ra
 * khỏi một tính năng hiển thị.
 */
const ASCII = [...emoticon]
  .flatMap((e) => e.emoticons.map((token) => ({ token, emoji: e.emoji })))
  .sort((a, b) => b.token.length - a.token.length)

const ASCII_MAP = new Map(ASCII.map((e) => [e.token, e.emoji]))
// String.raw để `\s` tới được RegExp: trong một chuỗi JS thường, '\s' chỉ là
// chữ 's'. Lỗi đó im lặng — biểu thức vẫn biên dịch, chỉ khớp sai.
const EMOTICON = new RegExp(
  String.raw`(^|\s)(` +
    ASCII.map((e) => e.token.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)).join('|') +
    String.raw`)(?=\s|$)`,
  'g',
)

/**
 * expandText đổi shortcode chuẩn và mặt cười kiểu chữ thành emoji Unicode.
 *
 * Shortcode KHÔNG có trong bộ chuẩn được giữ nguyên — node-emoji trả lại chính
 * chuỗi đó, nên `:khong-co-that:` và `12:30:` đi qua nguyên vẹn.
 */
export function expandText(s: string): string {
  return emojify(s).replace(
    EMOTICON,
    (_m: string, lead: string, token: string) => lead + (ASCII_MAP.get(token) ?? token),
  )
}

// Dải Unicode của emoji, dùng để nhận ra tin nhắn CHỈ có emoji.
const UNICODE_EMOJI = /^[\p{Extended_Pictographic}\p{Emoji_Component}\s]+$/u

/**
 * emojiOnly cho biết tin nhắn chỉ gồm emoji.
 *
 * Dùng để phóng to chúng, đúng như mọi ứng dụng chat khác. Không phải trang
 * trí: một 👍 cỡ chữ thường trong một cuộc họp nhìn như một lỗi hiển thị.
 */
export function emojiOnly(text: string): boolean {
  const stripped = expandText(text.replace(TOKEN, (m, name: string) => (BY_NAME.has(name) ? '' : m)))
    .trim()
  return stripped === '' || UNICODE_EMOJI.test(stripped)
}
